import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const SUPPORTED = ["en"];
const STORAGE_KEY = "adelina_lang";
const USER_OVERRIDE_KEY = "adelina_lang_user_set";

/** Read the user's explicit choice if they've clicked the language switcher. */
function readSavedLang() {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  // Migration: old 'ms' locale bumps to 'id'.
  if (raw === "ms") return "en";
  return SUPPORTED.includes(raw) ? raw : null;
}

const userChosen = typeof window !== "undefined" && localStorage.getItem(USER_OVERRIDE_KEY) === "1";
const savedLang = readSavedLang();
// Boot with saved lang if any, else "en" — will be updated post-settings-fetch below.
const bootLang = savedLang || "en";

i18n.use(initReactI18next).init({
  resources: { en: { translation: {} } },
  supportedLngs: SUPPORTED,
  lng: bootLang,
  fallbackLng: "en",
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

async function fetchBundle(lang) {
  try {
    const { data } = await axios.get(`${BACKEND}/api/i18n/${lang}`);
    return data?.strings || {};
  } catch {
    return {};
  }
}

export async function loadLanguage(lang) {
  if (!SUPPORTED.includes(lang)) return;
  const strings = await fetchBundle(lang);
  i18n.addResourceBundle(lang, "translation", strings, true, true);
  await i18n.changeLanguage(lang);
}

export async function changeLanguage(lang) {
  if (!SUPPORTED.includes(lang)) return;
  await loadLanguage(lang);
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, lang);
    // Mark as user-chosen so admin default doesn't override on next visit.
    localStorage.setItem(USER_OVERRIDE_KEY, "1");
  }
}

/**
 * Resolve the initial language:
 *   1. If the user has explicitly clicked the LanguageSwitcher → their choice wins.
 *   2. Else → fetch `/settings/public` and use `default_language` set by admin.
 *   3. Fallback → English.
 * Then load the bundle so first paint already has translations.
 */
async function bootstrapLanguage() {
  let lang = bootLang;
  if (!userChosen) {
    try {
      const { data } = await axios.get(`${BACKEND}/api/settings/public`);
      const adminDefault = (data?.default_language || "").toLowerCase();
      if (SUPPORTED.includes(adminDefault)) lang = adminDefault;
    } catch {
      // Settings fetch failed — keep saved/default
    }
  }
  await loadLanguage(lang);
}

/** Awaited by index.js before React root renders so first paint already
 *  has translations — eliminates the raw-key flash. */
export const i18nReady = bootstrapLanguage();

/** Reload the current language bundle (useful after admin edits translations). */
export async function reloadCurrentLanguage() {
  await loadLanguage(i18n.language || bootLang);
}

/** Called by admin after saving default_language so page reflects it immediately
 *  (only if user hasn't chosen manually). */
export async function applyAdminDefaultLanguage(lang) {
  if (!SUPPORTED.includes(lang)) return;
  if (userChosen) return; // respect user's explicit choice
  await loadLanguage(lang);
}

// Expose a global handle so the admin panel can trigger a live reload
// after saving text overrides without a full page refresh.
if (typeof window !== "undefined") {
  window.__reloadI18n = reloadCurrentLanguage;
  window.__applyAdminDefaultLanguage = applyAdminDefaultLanguage;
}

export const SUPPORTED_LANGS = SUPPORTED;
export default i18n;
